import {
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import useWebSocket from './use-websocket';

import AuthContext from '@/contexts/auth';
import type {
    WhatsappConversationListResponse,
    WhatsappMessageListResponse,
    WhatsappWsEvent,
} from '@/features/chat/types';
import { getTokens } from '@/lib/utils';

function buildWsUrl(): string | null {
    const { accessToken } = getTokens();
    if (!accessToken) return null;

    const serverUrl = import.meta.env
        .VITE_APP_SERVER_URL as string;
    if (!serverUrl) return null;

    const wsBase = serverUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://')
        .replace(/\/api\/?$/, '');

    return `${wsBase}/ws/notifications/?token=${accessToken}&kind=whatsapp`;
}

// Mounted once, globally (see Layout) alongside the other notification
// hooks. Keeps the conversation list, the open thread, and the sidebar
// unread badge in sync with the four `kind: "whatsapp"` events documented
// in whatsapp-frontend-integration.md.
export default function useWhatsappNotifications() {
    const queryClient = useQueryClient();
    const { state } = useContext(AuthContext);

    const handledRef = useRef<Map<string, number>>(new Map());

    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when auth changes
    const wsUrl = useMemo(() => buildWsUrl(), [state]);

    const handleMessage = useCallback(
        (raw: unknown) => {
            const msg = raw as WhatsappWsEvent;
            if (msg?.kind !== 'whatsapp' || !msg.event) return;

            const dedupeId = msg.event === 'whatsapp_message_status' ? msg.message.uuid : msg.conversation.uuid;
            const dedupeKey = `${msg.event}:${dedupeId}`;
            const now = Date.now();
            const last = handledRef.current.get(dedupeKey);
            if (last && now - last < 2000) return;
            handledRef.current.set(dedupeKey, now);

            Array.from(handledRef.current.entries())
                .filter(([, ts]) => now - ts > 10000)
                .forEach(([key]) => handledRef.current.delete(key));

            // Patch the affected caches in place rather than refetching the
            // whole list/thread, per whatsapp-frontend-integration.md §5-§8.
            if (msg.event === 'whatsapp_message_received' || msg.event === 'whatsapp_message_sent') {
                const { conversation, message } = msg;
                queryClient.setQueryData<WhatsappMessageListResponse>(
                    ['whatsapp-messages', conversation.uuid],
                    (old) => {
                        if (!old || old.results.some((m) => m.uuid === message.uuid)) return old;
                        return { ...old, results: [message, ...old.results], count: old.count + 1 };
                    },
                );
                queryClient.setQueriesData<WhatsappConversationListResponse>(
                    { predicate: (q) => q.queryKey[0] === 'whatsapp-conversations' },
                    (old) => {
                        if (!old) return old;
                        if (old.results.some((c) => c.uuid === conversation.uuid)) {
                            return {
                                ...old,
                                results: old.results.map(
                                    (c) => (c.uuid === conversation.uuid ? conversation : c),
                                ),
                            };
                        }
                        // A brand-new contact: only the default (page 1, no
                        // search) view can safely gain a row without risking
                        // a duplicate once the user pages or searches.
                        if (old.current_page === 1) {
                            return {
                                ...old,
                                results: [conversation, ...old.results],
                                count: old.count + 1,
                            };
                        }
                        return old;
                    },
                );
                if (msg.event === 'whatsapp_message_received') {
                    const { display_name: name, wa_id: waId } = conversation.contact;
                    toast.info(`New WhatsApp message from ${name || waId}`);
                    queryClient.invalidateQueries({ queryKey: ['notifications'] });
                    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
                }
            } else if (msg.event === 'whatsapp_message_status') {
                queryClient.setQueryData<WhatsappMessageListResponse>(
                    ['whatsapp-messages', msg.conversation_uuid],
                    (old) => {
                        if (!old) return old;
                        const statusUpdate = msg.message;
                        const patchStatus = (m: (typeof old.results)[number]) => (
                            m.uuid === statusUpdate.uuid ? { ...m, ...statusUpdate } : m
                        );
                        return { ...old, results: old.results.map(patchStatus) };
                    },
                );
            } else if (msg.event === 'whatsapp_conversation_read') {
                queryClient.setQueriesData<WhatsappConversationListResponse>(
                    { predicate: (q) => q.queryKey[0] === 'whatsapp-conversations' },
                    (old) => {
                        if (!old) return old;
                        const readConversation = msg.conversation;
                        const patchRead = (c: (typeof old.results)[number]) => (
                            c.uuid === readConversation.uuid ? { ...c, ...readConversation } : c
                        );
                        return { ...old, results: old.results.map(patchRead) };
                    },
                );
            }

            queryClient.invalidateQueries({ queryKey: ['whatsapp-unread-count'] });
        },
        [queryClient],
    );

    return useWebSocket({
        url: wsUrl || '',
        onMessage: handleMessage,
        enabled: !!wsUrl,
    });
}
